import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { 
  useTheme, 
  useMediaQuery, 
  Box,
  Tooltip, 
  TextField,
  Button,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { de, enGB } from 'date-fns/locale';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexBetween from 'components/FlexBetween';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import CustomDatePicker from "components/CustomDatePicker";


const ParamPage = () => {
    useAuthCheck(); 
    const navigate = useNavigate();
    const locales = { 'en-us': undefined, 'en-gb': enGB, de };
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const [licenseKey, setLicenseKey] = useState("");
    
    
    const getEndOfNextYear = () => {
        const now = new Date();
        return new Date(now.getFullYear() + 1, 11, 31);
    };

    const initialValues = useMemo(() => ({
        company: "DigitAir",
        domain: "https://digitair.onrender.com",
        expiry: getEndOfNextYear(),
        issuedBy: "thanhtrieu",
    }), []);
    
    const checkoutSchema = yup.object().shape({
        company: yup.string().required('Required'),
        expiry: yup.date().required('Required'),
    });

    const handleFormSubmit = async (values, { setSubmitting }) => {
        try {
            const url = '/generateLicense';
            const method = "POST";

            const response = await api({
                method,
                url,
                data: values,
            });            

            if ([200, 201].includes(response.status)) {
                const { licenseKey } = response.data;
                if (licenseKey) {
                    setLicenseKey(licenseKey);
                    toast.success("License generated successfully.");
                } else {
                    toast.success("License generated but no key returned!");
                }
                return;
            }           
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to generate license!");
        } finally {
            setSubmitting(false);
        }
    };


    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header
                    title="License Generator"
                    subtitle={`Create`}
                />
                <Box display="flex" alignItems="center" gap={1}>
                    <Tooltip title="Log out" arrow>
                        <span>
                            <StyledButton 
                                onClick={() => navigate("/")} 
                            >
                                <LogoutIcon />
                            </StyledButton>
                        </span>
                    </Tooltip>
                </Box>
            </FlexBetween>
            <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                <LocalizationProvider 
                    dateAdapter={AdapterDateFns}
                    adapterLocale={locales['en-gb']}
                >
                    <Formik
                        initialValues={initialValues}
                        validationSchema={checkoutSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleBlur,
                            handleChange,
                            handleSubmit,
                            setFieldValue,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <Box
                                    display="grid"
                                    gap={2}
                                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                    sx={{ '& > div': { gridColumn: isDesktop ? undefined : 'span 12' } }}
                                >
                                    <TextField
                                        required
                                        fullWidth
                                        variant="filled"
                                        type="text"
                                        label="Company"
                                        name="company"
                                        value={values.company}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        error={!!touched.company && !!errors.company}
                                        helperText={touched.company && errors.company}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        label="Domain"
                                        name="domain"
                                        value={values.domain}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        error={!!touched.domain && !!errors.domain}
                                        helperText={touched.domain && errors.domain}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <CustomDatePicker
                                        label="License Expiry"
                                        name="expiry"
                                        value={values.expiry}
                                        minDate={new Date()}
                                        onChange={setFieldValue}
                                        touched={touched}
                                        errors={errors}
                                        sx={{ gridColumn: "span 6" }}
                                    />
                                    
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        label="Issued By"
                                        name="issuedBy"
                                        value={values.issuedBy}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        error={!!touched.issuedBy && !!errors.issuedBy}
                                        helperText={touched.issuedBy && errors.issuedBy}
                                        sx={{ gridColumn: 'span 6' }}
                                    />
                                    
                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ gridColumn: "span 12", height: 42 }}
                                    >
                                        Submit
                                    </Button>

                                    {licenseKey && (
                                        <Box gridColumn="span 12" mt={2}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                variant="outlined"
                                                label="License Key"
                                                value={licenseKey}
                                                slotProps={{
                                                    input: {
                                                        readOnly: true,
                                                    },
                                                }}
                                            />

                                            <Button
                                                fullWidth
                                                variant="contained"
                                                color="primary"
                                                sx={{ gridColumn: "span 12", height: 42, mt: 2 }}
                                                onClick={() => {
                                                    navigator.clipboard.writeText(licenseKey);
                                                    toast.success("Copied license to clipboard.");
                                                }}
                                            >
                                                Copy License Key
                                            </Button>
                                        </Box>
                                    )}

                                </Box>
                            </form>
                        )}
                    </Formik>
                </LocalizationProvider>                    
            </Box>
        </Box>
    );
};

export default ParamPage;
